import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Camera, Check } from "lucide-react";
import { Alert, Button, Card, CardBody, ScrollablePage, Spinner, useToast } from "@/ui";
import { useTenant } from "@/tenant/TenantProvider";
import { KarigarHeader } from "@/components/KarigarShell";
import { useLanguage } from "@/i18n/language";
import { api, ApiClientError } from "@/lib/api";
import { KarigarPlaceFields, type KarigarPlace } from "@/components/KarigarPlaceFields";

interface LinkInfo {
  orgName: string;
  counterName: string;
  /** Masked by the server: `••••••3210`. */
  phone: string;
}

/**
 * Opened from the personal link a karigar gets back on WhatsApp after sending
 * the counter poster's message (D-22). The link already proves their number,
 * so there is no OTP. Bilingual and mobile-first (D-11, WIREFRAMES) — every
 * label carries Hindi first, English second.
 */
export function KarigarRegistrationPage() {
  const { orgSlug = "", token = "" } = useParams<{ orgSlug: string; token: string }>();
  const toast = useToast();
  const { payload } = useTenant();
  const { t } = useLanguage("hi");

  const [info, setInfo] = useState<LinkInfo | null>(null);
  /** The link cannot be used: expired, already used, or the shop was suspended. */
  const [blocked, setBlocked] = useState<string | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const [name, setName] = useState("");
  const [place, setPlace] = useState<KarigarPlace>({ city: "", state: "", pincode: "", district: "", village: "", address: "" });
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [photoChoicesOpen, setPhotoChoicesOpen] = useState(false);
  const galleryInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [displayId, setDisplayId] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const expired = t(
    "यह लिंक समाप्त हो चुका है। नया लिंक पाने के लिए दुकान का QR फिर से स्कैन करें।",
    "This link has expired. Scan the shop's QR again to get a new one.",
  );

  /** A 404 is always "expired or used"; anything else the server explains itself. */
  function block(err: unknown): void {
    if (err instanceof ApiClientError && err.status === 404) setBlocked(expired);
    else setBlocked(err instanceof ApiClientError ? err.message : "Something went wrong. Please try again.");
  }

  useEffect(() => {
    let cancelled = false;
    api
      .get<LinkInfo>(`/public/orgs/${orgSlug}/register/${token}`)
      .then((res) => {
        if (!cancelled) setInfo(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) block(err);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug, token]);

  function handlePhotoChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    setPhotoChoicesOpen(false);
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result));
    reader.readAsDataURL(file);
  }

  async function uploadPhotoIfNeeded(): Promise<string | undefined> {
    if (!photoDataUrl) return undefined;
    try {
      const result = await api.post<{ photoKey: string }>(`/public/orgs/${orgSlug}/registration/photo`, {
        dataUrl: photoDataUrl,
      });
      return result.photoKey;
    } catch {
      toast.error("Photo could not be uploaded. Continuing without it.");
      return undefined;
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      const photoKey = await uploadPhotoIfNeeded();
      const result = await api.post<{ id: string; displayId: string | null }>(
        `/public/orgs/${orgSlug}/register/${token}`,
        { name, photoKey, ...place },
      );
      setDisplayId(result.displayId);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) setAlreadyRegistered(true);
      else if (err instanceof ApiClientError && (err.status === 404 || err.status === 403)) block(err);
      else if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const formValid = name.trim().length >= 2 && place.address.trim().length >= 4
    && Boolean(place.city.trim() && place.state) && /^[1-9]\d{5}$/.test(place.pincode);
  const signIn = (
    <Link
      className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-sm font-medium text-on-brand hover:bg-brand-hover"
      to={`/${orgSlug}/login/karigar`}
    >
      {t("ऐप खोलें", "Open the app")}
    </Link>
  );

  return (
    /* D-15 exempts public screens — this form is filled on a phone and must
       scroll. See AuthLayout for the same marker. */
    <ScrollablePage>
    <div className="min-h-dvh bg-surface pb-10">
      {/* The same branded band as the karigar app, so the first screen a
          carpenter ever sees already looks like the app they will use. */}
      <KarigarHeader
        title={
          info
            ? t(`${info.counterName} में पंजीकरण`, `Register with ${info.counterName}`)
            : t("पंजीकरण", "Registration")
        }
      />
      <div className="relative mx-auto -mt-10 w-full max-w-md px-4 animate-rise">
      <Card>
        <CardBody className="flex flex-col gap-6 px-5 py-7">

          {blocked ? (
            <Alert kind="danger">{blocked}</Alert>
          ) : alreadyRegistered ? (
            <div className="flex flex-col gap-2 text-center">
              <p className="text-sm text-ink">
                {t("यह नंबर पहले से रजिस्टर है।", "This number is already registered.")}
              </p>
              {signIn}
            </div>
          ) : done ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <span aria-hidden="true" className="grid size-14 place-items-center rounded-full bg-success text-white shadow-[0_0_0_8px_color-mix(in_srgb,var(--gq-success)_15%,transparent)]">
                <Check className="size-7" strokeWidth={2.5} />
              </span>
              <h2 className="mt-1 font-title text-xl font-semibold text-ink">
                {t("पंजीकरण पूरा हुआ!", "Registration submitted!")}
              </h2>
              {displayId ? <p className="font-mono text-sm text-muted">{displayId}</p> : null}

              <p className="mt-2 text-sm text-ink">
                {t(
                  "आप अभी से कोड स्कैन कर सकते हैं। मंज़ूरी मिलते ही QCoin रिडीम कर सकेंगे।",
                  "You can start scanning codes now. You can redeem QCoin once you are approved.",
                )}
              </p>
              <p className="text-sm text-muted">
                {t("मंज़ूरी पर WhatsApp आएगा।", "You will get a WhatsApp message once approved.")}
              </p>
              {signIn}
            </div>
          ) : !info ? (
            <div className="grid place-items-center py-6">
              <Spinner />
            </div>
          ) : (
            <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink" htmlFor="reg-name">
                  {t("नाम", "Name")}
                </label>
                <input
                  className="h-11 rounded-md border border-rule bg-raised px-3 text-ink shadow-control transition-[border-color,box-shadow] placeholder:text-muted/60 hover:border-ink/25 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none"
                  id="reg-name"
                  onChange={(e) => setName(e.target.value)}
                  value={name}
                />
                {fields.name ? <p className="text-sm text-danger">{fields.name}</p> : null}
              </div>

              {/* From the WhatsApp message itself, so it cannot be edited here. */}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t("मोबाइल नंबर", "Mobile")}</span>
                <p className="flex h-11 items-center rounded-md border border-rule bg-surface px-3 text-sm text-muted">
                  +91 {info.phone}
                </p>
              </div>

              <KarigarPlaceFields language="hi" addressRequired value={place} errors={fields} onChange={(patch) => setPlace((p) => ({ ...p, ...patch }))} />


              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t("फोटो", "Photo")}</span>
                <input accept="image/*" className="hidden" onChange={handlePhotoChange} ref={galleryInput} type="file" />
                <input accept="image/*" capture="environment" className="hidden" onChange={handlePhotoChange} ref={cameraInput} type="file" />
                <button
                  aria-controls="karigar-photo-choices"
                  aria-expanded={photoChoicesOpen}
                  className="inline-flex w-fit items-center gap-2 rounded-md border border-rule bg-raised px-3.5 py-2 font-title text-sm font-semibold text-ink shadow-control transition-colors hover:border-brand-border hover:text-brand-ink"
                  onClick={() => setPhotoChoicesOpen((open) => !open)}
                  type="button"
                >
                  {photoDataUrl ? (
                    <img alt="" className="size-6 rounded object-cover" src={photoDataUrl} />
                  ) : (
                    <Camera aria-hidden="true" className="size-5" />
                  )}
                  {photoDataUrl ? t("फोटो बदलें", "Change photo") : t("फोटो जोड़ें", "Add photo")}
                </button>
                {photoChoicesOpen ? (
                  <div className="flex w-fit flex-col gap-1 rounded-md border border-rule bg-raised p-1 shadow-control" id="karigar-photo-choices" role="group" aria-label={t("फोटो कैसे जोड़ें", "How to add a photo")}>
                    <button className="rounded px-3 py-2 text-left text-sm text-ink hover:bg-surface" onClick={() => galleryInput.current?.click()} type="button">
                      {t("फोटो चुनें", "Choose photo")}
                    </button>
                    <button className="rounded px-3 py-2 text-left text-sm text-ink hover:bg-surface" onClick={() => cameraInput.current?.click()} type="button">
                      {t("कैमरे से फोटो लें", "Take photo")}
                    </button>
                  </div>
                ) : null}
                {fields.photoKey ? <p className="text-sm text-danger">{fields.photoKey}</p> : null}
              </div>

              <Button block disabled={!formValid} loading={submitting} size="lg" type="submit">
                {t("रजिस्टर करें", "Register")}
              </Button>
            </form>
          )}

          {/* The platform name, not a hardcoded one: an org that renamed the
              product still gets attributed to whoever runs the platform. */}
          <p className="text-center text-xs text-muted">
            Powered by {payload.platformAppName}
          </p>
        </CardBody>
      </Card>
      </div>
    </div>
    </ScrollablePage>
  );
}
